import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { PermissionGate } from "@/components/PermissionGate";
import { useJobActions } from "@/hooks/useJobActions";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, AlertTriangle, Shield } from "lucide-react";
import { toast } from "sonner";
import { PhotoCapture } from "@/components/qa/PhotoCapture";
import { YesNoToggle } from "@/components/qa/YesNoToggle";
import { ChecklistItem } from "@/components/qa/ChecklistItem";
import { SectionDivider } from "@/components/qa/SectionDivider";
import { QAService, uploadQAPhoto } from "@/services/qa.service";
import { JobService } from "@/services/job.service";
import type { Job } from "@/data/types";
import { validateQAPrerequisites } from "@/data/qaBlockers";
import { getJobBlockers } from "@/data/workflowBlockers";
import { migrationBypasses } from "@/data/import/migrationVisibilityGuard";

const SECTIONS = [
  { id: "arrival", label: "Arrival" },
  { id: "before", label: "Before" },
  { id: "progress", label: "Progress" },
  { id: "checklist", label: "Checklist" },
  { id: "final-photos", label: "Final" },
  { id: "confirm", label: "Confirm" },
];

function getJobDisplayRef(job: Job): string {
  if (typeof job.jobNumber === "string" && job.jobNumber.trim()) {
    return job.jobNumber.trim();
  }
  return job.id;
}

function isMaterialQaReady(materialReadiness: string | undefined): boolean {
  return ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(String(materialReadiness ?? "").trim());
}

function getQaEntryBlockers(job: Job, openIssueCount: number): string[] {
  const reasons: string[] = [];
  const hasCrew = Boolean((job.assignedCrew && job.assignedCrew.trim()) || (job.assignedSubcontractor && job.assignedSubcontractor.trim()));
  const hasSchedule = Boolean(job.scheduledInstallDate && String(job.scheduledInstallDate).trim());

  if (!isMaterialQaReady(job.materialReadiness) && !migrationBypasses(job, "ordering")) reasons.push("Materials are not install-ready");
  if (!hasSchedule) reasons.push("Install date is not scheduled");
  if (!hasCrew) reasons.push("Crew / subcontractor is not assigned");
  if (openIssueCount > 0) reasons.push("Job has unresolved issues");

  return reasons;
}

export default function QAForm() {
  const navigate = useNavigate();
  const { jobs, setJobs, issues, setIssues } = useAppData();
  const { completeQA: orchestratedCompleteQA } = useJobActions();
  const { jobId: routeJobId } = useParams<{ jobId?: string }>();
  const [searchParams] = useSearchParams();
  const [eligibleJobs, setEligibleJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState("");
  const [installerName, setInstallerName] = useState("");
  const [arrivalTime, setArrivalTime] = useState("");
  const [customerPresent, setCustomerPresent] = useState<boolean | null>(null);

  const [drainCorrect, setDrainCorrect] = useState(false);
  const [noLeaks, setNoLeaks] = useState(false);
  const [glassAligned, setGlassAligned] = useState(false);
  const [thresholdPitched, setThresholdPitched] = useState(false);
  const [areaCleaned, setAreaCleaned] = useState(false);
  const [protectionRemoved, setProtectionRemoved] = useState(false);

  const [issueEncountered, setIssueEncountered] = useState<boolean | null>(null);
  const [issueNotes, setIssueNotes] = useState("");
  const [jobComplete, setJobComplete] = useState<boolean | null>(null);
  const [unresolvedIssues, setUnresolvedIssues] = useState<boolean | null>(null);
  const [materialsUsed, setMaterialsUsed] = useState("");
  const [finalComments, setFinalComments] = useState("");

  // photos: key → blob URL for immediate preview (never stored to DB)
  const [photos, setPhotos] = useState<Record<string, string>>({});
  
  // photoPaths: key → storage path (what gets saved to DB)
  const [photoPaths, setPhotoPaths] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  // Derive a stable submission ID for this session
  const [submissionId] = useState(
    () => `QA-${Date.now().toString().slice(-6)}`
  );
  const draftStorageKey = useMemo(
    () => (selectedJob ? `qa-form-draft:${selectedJob}` : ""),
    [selectedJob],
  );

  // Load eligible jobs from Supabase
  const loadJobs = useCallback(async () => {
    const { data } = await JobService.getAll();
    if (data) setEligibleJobs(data);
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Pre-select job when navigated from JobDetail (?jobId=<uuid>)
  const jobIdParam = searchParams.get("jobId") ?? routeJobId ?? null;
  useEffect(() => {
    if (eligibleJobs.length === 0 || !jobIdParam || selectedJob) return;
    setSelectedJob(jobIdParam);
  }, [eligibleJobs, jobIdParam]);

  const openIssuesByJob = useMemo(() => {
    const map = new Map<string, number>();
    for (const issue of issues) {
      if (issue.resolved) continue;
      map.set(issue.jobId, (map.get(issue.jobId) ?? 0) + 1);
    }
    return map;
  }, [issues]);

  const selectedJobData = eligibleJobs.find((j) => j.id === selectedJob);
  const selectedJobBlockers = useMemo(() => {
    if (!selectedJobData) return [];
    const openIssueCount = openIssuesByJob.get(selectedJobData.id) ?? 0;
    return getQaEntryBlockers(selectedJobData, openIssueCount);
  }, [selectedJobData, openIssuesByJob]);
  const selectedJobHasQaGateBlock = selectedJobBlockers.length > 0;

  const qaEligibleJobs = useMemo(() => {
    return eligibleJobs.filter((job) => {
      const openIssueCount = openIssuesByJob.get(job.id) ?? 0;
      return getQaEntryBlockers(job, openIssueCount).length === 0;
    });
  }, [eligibleJobs, openIssuesByJob]);

  useEffect(() => {
    if (!selectedJobData || !selectedJobHasQaGateBlock) return;
    if (!jobIdParam || selectedJobData.id !== jobIdParam) return;
    toast.error(`QA blocked: ${selectedJobBlockers.join(" · ")}`);
    navigate(`/jobs/${selectedJobData.id}`);
  }, [
    jobIdParam,
    navigate,
    selectedJobData,
    selectedJobHasQaGateBlock,
    selectedJobBlockers,
  ]);

  // Rehydrate in-progress form values for the selected job.
  useEffect(() => {
    if (!draftStorageKey) return;
    try {
      const raw = localStorage.getItem(draftStorageKey);
      if (!raw) return;
      const draft = JSON.parse(raw) as Record<string, unknown>;
      setInstallerName(String(draft.installerName ?? ""));
      setArrivalTime(String(draft.arrivalTime ?? ""));
      setCustomerPresent(draft.customerPresent === null ? null : Boolean(draft.customerPresent));
      setDrainCorrect(Boolean(draft.drainCorrect));
      setNoLeaks(Boolean(draft.noLeaks));
      setGlassAligned(Boolean(draft.glassAligned));
      setThresholdPitched(Boolean(draft.thresholdPitched));
      setAreaCleaned(Boolean(draft.areaCleaned));
      setProtectionRemoved(Boolean(draft.protectionRemoved));
      setIssueEncountered(draft.issueEncountered === null ? null : Boolean(draft.issueEncountered));
      setIssueNotes(String(draft.issueNotes ?? ""));
      setJobComplete(draft.jobComplete === null ? null : Boolean(draft.jobComplete));
      setUnresolvedIssues(draft.unresolvedIssues === null ? null : Boolean(draft.unresolvedIssues));
      setMaterialsUsed(String(draft.materialsUsed ?? ""));
      setFinalComments(String(draft.finalComments ?? ""));
      setPhotoPaths((draft.photoPaths as Record<string, string>) ?? {});
      setPhotos((draft.photos as Record<string, string>) ?? {});
    } catch {
      // Ignore malformed drafts and continue with blank form.
    }
  }, [draftStorageKey]);

  // Persist draft continuously while filling QA.
  useEffect(() => {
    if (!draftStorageKey) return;
    const payload = {
      installerName,
      arrivalTime,
      customerPresent,
      drainCorrect,
      noLeaks,
      glassAligned,
      thresholdPitched,
      areaCleaned,
      protectionRemoved,
      issueEncountered,
      issueNotes,
      jobComplete,
      unresolvedIssues,
      materialsUsed,
      finalComments,
      photoPaths,
      photos,
      updatedAt: Date.now(),
    };
    localStorage.setItem(draftStorageKey, JSON.stringify(payload));
  }, [
    draftStorageKey,
    installerName,
    arrivalTime,
    customerPresent,
    drainCorrect,
    noLeaks,
    glassAligned,
    thresholdPitched,
    areaCleaned,
    protectionRemoved,
    issueEncountered,
    issueNotes,
    jobComplete,
    unresolvedIssues,
    materialsUsed,
    finalComments,
    photoPaths,
    photos,
  ]);

  const handlePhotoUpload = async (key: string, file: File) => {
    if (!selectedJob) {
      toast.error("Select a job before uploading photos");
      return;
    }
    // Show blob URL immediately so the preview appears without waiting for the upload
    const blobUrl = URL.createObjectURL(file);
    setPhotos((p) => ({ ...p, [key]: blobUrl }));
    setUploading((u) => ({ ...u, [key]: true }));
    const path = await uploadQAPhoto(file, selectedJob, submissionId, key);
    setUploading((u) => ({ ...u, [key]: false }));
    if (path) {
      setPhotoPaths((p) => ({ ...p, [key]: path }));
      toast.success("Photo uploaded");
    } else {
      // Revert optimistic preview on failure
      setPhotos((p) => { const n = { ...p }; delete n[key]; return n; });
      toast.error("Photo upload failed — check your connection");
    }
  };

  const progress = useMemo(() => {
    const checks = {
      arrival: !!(selectedJob && installerName && arrivalTime && customerPresent !== null),
      before: !!(photos["drop_cloths"] && photos["before_full"] && photos["existing_damage"] && photos["plumbing_before"]),
      progress: !!(photos["mid_install"] && issueEncountered !== null && (issueEncountered === false || issueNotes)),
      checklist: drainCorrect && noLeaks && glassAligned && thresholdPitched && areaCleaned && protectionRemoved,
      "final-photos": !!(photos["completed_full"] && photos["detail_shots"] && photos["plumbing_after"] && photos["clean_workspace"]),
      confirm: jobComplete !== null && unresolvedIssues !== null,
    };
    const done = Object.values(checks).filter(Boolean).length;
    return { checks, done, total: 6, pct: Math.round((done / 6) * 100) };
  }, [selectedJob, installerName, arrivalTime, customerPresent, photos, issueEncountered, issueNotes, drainCorrect, noLeaks, glassAligned, thresholdPitched, areaCleaned, protectionRemoved, jobComplete, unresolvedIssues]);

  const resetForm = () => {
    setSelectedJob(""); setInstallerName(""); setArrivalTime(""); setCustomerPresent(null);
    setIssueEncountered(null); setIssueNotes(""); setPhotos({}); setPhotoPaths({});
    setDrainCorrect(false); setNoLeaks(false); setGlassAligned(false);
    setThresholdPitched(false); setAreaCleaned(false); setProtectionRemoved(false);
    setJobComplete(null); setUnresolvedIssues(null); setMaterialsUsed(""); setFinalComments("");
  };

  const handleSubmit = async () => {
    if (!selectedJob || !installerName) {
      toast.error("Please select a job and enter your name.");
      return;
    }
    if (!selectedJobData) {
      toast.error("Selected job could not be loaded.");
      return;
    }
    if (!qaReadiness.canProceed) {
      const blockerText = qaReadiness.blockers.map((b) => b.label).join(" · ");
      toast.error(`QA blocked: ${blockerText}`);
      return;
    }
    if (selectedJobHasQaGateBlock) {
      toast.error(`QA blocked: ${selectedJobBlockers.join(" · ")}`);
      return;
    }
    const workflowBlockers = getJobBlockers(selectedJobData, issues.filter((i) => i.jobId === selectedJobData.id && !i.resolved))
      .filter((b) => b.severity === "critical")
      .map((b) => b.label);
    if (workflowBlockers.length > 0) {
      toast.error(`QA blocked: ${workflowBlockers.join(" · ")}`);
      return;
    }
    if (progress.pct < 100) {
      toast.error("Please complete all sections before submitting.");
      return;
    }

    // const result = orchestratedCompleteQA(selectedJob, {
    //   passed: !issueEncountered,
    //   issueEncountered: !!issueEncountered,
    //   issueNotes: issueNotes || undefined,
    //   issuePhoto: photos["issue"] || undefined,
    //   installerName,
    // });

    // if (!result.success) {
    //   toast.error(result.error || "QA submission failed");
    //   return;
    // }
    
    setSubmitting(true);

    const { error } = await QAService.submit({
      submissionId,
      jobId: selectedJob,
      installerName,
      arrivalTime,
      customerPresent,
      checklist: { drainCorrect, noLeaks, glassAligned, thresholdPitched, areaCleaned, protectionRemoved },
      photos: photoPaths,
      issueEncountered,
      issueNotes,
      jobComplete,
      unresolvedIssues,
      materialsUsed,
      finalComments,
      status: "Submitted",
    });

    setSubmitting(false);

    if (error) {
      toast.error("Failed to submit QA report. Please try again.");
      return;
    }

    // Sync local AppDataContext so the rest of the UI reflects the stage change
    setJobs((prev) =>
      prev.map((j) =>
        j.id === selectedJob
          ? { ...j, currentStage: "QA Review" as const, qaSubmitted: true, qaStatus: "Submitted" as const, issueFlag: issueEncountered || j.issueFlag, installStatus: "Install Complete" as const }
          : j
      )
    );

    if (draftStorageKey) localStorage.removeItem(draftStorageKey);
    toast.success("QA submitted! Job moved to QA Review.");
    resetForm();
    loadJobs();
    navigate(`/jobs/${selectedJob}`);
  };

  const isUploading = Object.values(uploading).some(Boolean);

  // Compute QA readiness for selected job
  const qaReadiness = useMemo(() => {
    if (!selectedJobData) return { canProceed: true, blockers: [], warnings: [] };
    return validateQAPrerequisites(selectedJobData);
  }, [selectedJobData]);

  return (
    <div className="max-w-lg mx-auto pb-8">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b -mx-4 md:-mx-6 px-4 md:px-6 py-3 mb-4">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div>
            <h1 className="text-base font-bold tracking-tight">Install QA</h1>
            <p className="text-[11px] text-muted-foreground">
              {selectedJobData ? `${getJobDisplayRef(selectedJobData)} · ${selectedJobData.customerName}` : "Field checklist"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <p className="text-xs font-bold text-primary">{progress.pct}%</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">{progress.done}/{progress.total}</p>
            </div>
            <div className="relative h-10 w-10">
              <svg className="h-10 w-10 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="hsl(var(--muted))" strokeWidth="3" />
                <circle
                  cx="18" cy="18" r="15.5" fill="none"
                  stroke="hsl(var(--primary))"
                  strokeWidth="3"
                  strokeDasharray={`${progress.pct} 100`}
                  strokeLinecap="round"
                  className="transition-all duration-500"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Section progress dots */}
        <div className="flex items-center gap-1.5 mt-2 max-w-lg mx-auto">
          {SECTIONS.map((s) => (
            <div key={s.id} className="flex-1 flex flex-col items-center gap-0.5">
              <div className={`h-1.5 w-full rounded-full transition-colors ${
                progress.checks[s.id as keyof typeof progress.checks] ? "bg-success" : "bg-muted"
              }`} />
              <span className="text-[8px] text-muted-foreground uppercase tracking-wider hidden sm:block">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* QA Data Flags */}
      {selectedJobData && (qaReadiness.blockers.length > 0 || qaReadiness.warnings.length > 0) && (
        <div className="rounded-xl border-2 border-warning/30 bg-warning/5 p-3 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-3.5 w-3.5 text-warning" />
            <span className="text-[10px] font-bold text-warning uppercase tracking-wider">QA Data Flags</span>
          </div>
          <ul className="text-[10px] text-muted-foreground space-y-0.5">
            {qaReadiness.blockers.map((b) => (
              <li key={b.name}>• {b.label}: {b.detail} (Required)</li>
            ))}
            {qaReadiness.warnings.map((w) => (
              <li key={w.name}>• {w.label}: {w.detail}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Compliance banner */}
      <div className="rounded-xl border-2 border-primary/20 bg-primary/[0.03] p-3 mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Shield className="h-3.5 w-3.5 text-primary" />
          <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Documentation Standard</span>
        </div>
        <p className="text-[10px] text-muted-foreground leading-relaxed">
          This QA form is required for all installs — internal crews and subcontractors. 100% completion required before job closure. Photos serve as legal and quality documentation.
        </p>
      </div>

      {selectedJobHasQaGateBlock && (
        <div className="rounded-xl border-2 border-destructive/30 bg-destructive/5 p-3 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
            <span className="text-[10px] font-bold text-destructive uppercase tracking-wider">QA Blocked</span>
          </div>
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            {selectedJobBlockers.join(" · ")}
          </p>
        </div>
      )}

      <div className="space-y-2">

        {/* ═══ 1. ARRIVAL ═══ */}
        <SectionDivider number={1} title="Arrival Info" done={progress.checks.arrival} />

        <div className="space-y-4 pl-2">
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Label className="text-sm font-medium">Job <span className="text-destructive font-bold">*</span></Label>
            </div>
              <Select value={selectedJob} onValueChange={setSelectedJob}>
                <SelectTrigger className="h-12 text-sm rounded-xl"><SelectValue placeholder="Select job..." /></SelectTrigger>
                <SelectContent>
                  {qaEligibleJobs.map((j) => (
                  <SelectItem key={j.id} value={j.id} className="text-sm py-2.5">{getJobDisplayRef(j)} — {j.customerName}</SelectItem>
                ))}
                </SelectContent>
              </Select>
          </div>

          <div>
            <Label className="text-sm font-medium">Installer / Crew Lead <span className="text-destructive font-bold">*</span></Label>
            <Input
              value={installerName}
              onChange={(e) => setInstallerName(e.target.value)}
              placeholder="Your name"
              className="h-12 text-sm rounded-xl mt-1.5"
            />
          </div>

          <div>
            <Label className="text-sm font-medium">Arrival Time <span className="text-destructive font-bold">*</span></Label>
            <Input
              type="time"
              value={arrivalTime}
              onChange={(e) => setArrivalTime(e.target.value)}
              className="h-12 text-sm rounded-xl mt-1.5"
            />
          </div>

          <YesNoToggle value={customerPresent} onChange={setCustomerPresent} label="Customer Present?" required />
        </div>

        {/* ═══ 2. BEFORE PHOTOS ═══ */}
        <SectionDivider number={2} title="Before-Work Photos" done={progress.checks.before} />

        <div className="grid grid-cols-2 gap-3 pl-2">
          <PhotoCapture label="Drop Cloths & Floor Protection" captured={!!photos["drop_cloths"]} url={photos["drop_cloths"]} onCapture={(f) => handlePhotoUpload("drop_cloths", f)} />
          <PhotoCapture label="Full Before Photo" captured={!!photos["before_full"]} url={photos["before_full"]} onCapture={(f) => handlePhotoUpload("before_full", f)} />
          <PhotoCapture label="Existing Damage / Problem Areas" captured={!!photos["existing_damage"]} url={photos["existing_damage"]} onCapture={(f) => handlePhotoUpload("existing_damage", f)} />
          <PhotoCapture label="Plumbing Before" captured={!!photos["plumbing_before"]} url={photos["plumbing_before"]} onCapture={(f) => handlePhotoUpload("plumbing_before", f)} />
        </div>

        {/* ═══ 3. PROGRESS ═══ */}
        <SectionDivider number={3} title="Install Progress" done={progress.checks.progress} />

        <div className="space-y-4 pl-2">
          <div className="grid grid-cols-2 gap-3">
            <PhotoCapture label="Mid-Install Progress" captured={!!photos["mid_install"]} url={photos["mid_install"]} onCapture={(f) => handlePhotoUpload("mid_install", f)} />
            {issueEncountered && <PhotoCapture label="Issue Photo" captured={!!photos["issue"]} url={photos["issue"]} onCapture={(f) => handlePhotoUpload("issue", f)} required={false} />}
          </div>

          <YesNoToggle value={issueEncountered} onChange={setIssueEncountered} label="Issue Encountered?" required />

          {issueEncountered && (
            <div className="rounded-xl border-2 border-warning/30 bg-warning/5 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-warning" />
                <span className="text-sm font-semibold text-warning">Describe the Issue</span>
              </div>
              <Textarea
                value={issueNotes}
                onChange={(e) => setIssueNotes(e.target.value)}
                placeholder="What went wrong? Be specific..."
                className="min-h-[80px] text-sm rounded-xl border-warning/30"
              />
            </div>
          )}
        </div>

        {/* ═══ 4. COMPLETION CHECKLIST ═══ */}
        <SectionDivider number={4} title="Completion Checklist" done={progress.checks.checklist} />

        <div className="space-y-2 pl-2">
          <ChecklistItem checked={drainCorrect} onChange={setDrainCorrect} label="Drain installed correctly" />
          <ChecklistItem checked={noLeaks} onChange={setNoLeaks} label="No leaks present" />
          <ChecklistItem checked={glassAligned} onChange={setGlassAligned} label="Glass aligned properly" />
          <ChecklistItem checked={thresholdPitched} onChange={setThresholdPitched} label="Threshold pitched correctly" />
          <ChecklistItem checked={areaCleaned} onChange={setAreaCleaned} label="Area cleaned" />
          <ChecklistItem checked={protectionRemoved} onChange={setProtectionRemoved} label="Protection removed properly" />
        </div>

        {/* ═══ 5. FINAL PHOTOS ═══ */}
        <SectionDivider number={5} title="Final Photos" done={progress.checks["final-photos"]} />

        <div className="grid grid-cols-2 gap-3 pl-2">
          <PhotoCapture label="Full Completed Project" captured={!!photos["completed_full"]} url={photos["completed_full"]} onCapture={(f) => handlePhotoUpload("completed_full", f)} />
          <PhotoCapture label="Detail Shots" captured={!!photos["detail_shots"]} url={photos["detail_shots"]} onCapture={(f) => handlePhotoUpload("detail_shots", f)} />
          <PhotoCapture label="Plumbing After" captured={!!photos["plumbing_after"]} url={photos["plumbing_after"]} onCapture={(f) => handlePhotoUpload("plumbing_after", f)} />
          <PhotoCapture label="Clean Workspace" captured={!!photos["clean_workspace"]} url={photos["clean_workspace"]} onCapture={(f) => handlePhotoUpload("clean_workspace", f)} />
        </div>

        {/* ═══ 6. FINAL CONFIRMATION ═══ */}
        <SectionDivider number={6} title="Final Confirmation" done={progress.checks.confirm} />

        <div className="space-y-4 pl-2">
          <YesNoToggle value={jobComplete} onChange={setJobComplete} label="Job Complete?" required />
          <YesNoToggle value={unresolvedIssues} onChange={setUnresolvedIssues} label="Unresolved Issues?" required />

          <div>
            <Label className="text-sm font-medium">Materials Used</Label>
            <Textarea
              value={materialsUsed}
              onChange={(e) => setMaterialsUsed(e.target.value)}
              placeholder="List materials used..."
              className="min-h-[70px] text-sm rounded-xl mt-1.5"
            />
          </div>

          <div>
            <Label className="text-sm font-medium">Final Comments</Label>
            <Textarea
              value={finalComments}
              onChange={(e) => setFinalComments(e.target.value)}
              placeholder="Anything else to note..."
              className="min-h-[70px] text-sm rounded-xl mt-1.5"
            />
          </div>
        </div>

        {/* SUBMIT */}
        <div className="pt-6">
          <PermissionGate permission="qa_create">
            <button
              onClick={handleSubmit}
              disabled={progress.pct < 100 || submitting || isUploading || !qaReadiness.canProceed}
              className={`w-full py-4 rounded-xl text-base font-bold transition-all active:scale-[0.98] ${
                progress.pct === 100 && !submitting && !isUploading && qaReadiness.canProceed
                  ? "bg-success text-success-foreground hover:bg-success/90 shadow-lg shadow-success/20"
                  : "bg-muted text-muted-foreground cursor-not-allowed"
              }`}
            >
              {submitting ? (
                "Submitting…"
              ) : isUploading ? (
                "Uploading photos…"
              ) : progress.pct === 100 && qaReadiness.canProceed ? (
                <span className="inline-flex items-center gap-2"><CheckCircle className="h-5 w-5" /> Submit QA Report</span>
              ) : !qaReadiness.canProceed ? (
                "Resolve QA blockers first"
              ) : (
                `Complete All Sections (${progress.pct}%)`
              )}
            </button>
          </PermissionGate>
        </div>
      </div>
    </div>
  );
}
